import {
  FunctionComponent,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Trans } from 'react-i18next';
import { EnvService } from '@hub/services/core/env-service';
import { trackMixpanel } from '@monorepo/client-common';
import { CircularProgress, Stack } from '@monorepo/nova-ui';
import { useLoading } from '@monorepo/react-hooks';
import {
  EventTypes,
  FailedMailOtpCodeReason,
  MailOtpSource,
  ReceivedMailState,
} from '@monorepo/types';
import { useStore } from '../../helpers/use-store';
import { CodeCheck } from './code-check';
import { FailedSendingMail } from './failed-sending-mail';
import { MailOtpStage } from './mail-otp-stage';
import { MailRejected } from './mail-rejected';
import { NoMoreRetries } from './no-more-retries';
import { NotReceivedMail } from './not-received-mail';

interface Props {
  verifiedCodeComponent: ReactNode;
  sendCodeStageHeader: string;
  sendCodeStageMessage: string;
  source: MailOtpSource;
  sourceId: string;
  displayName?: string;
  // Optional stages for payout flow
  payoutConfirmationStage?: ReactNode;
  withdrawalConfirmedStage?: ReactNode;
  onShowPayoutConfirmation?: () => void;
  onShowWithdrawalConfirmed?: () => void;
}

export const MailConfirmation: FunctionComponent<Props> = ({
  verifiedCodeComponent,
  sendCodeStageHeader,
  sendCodeStageMessage,
  source,
  sourceId,
  displayName,
}) => {
  const [receivedMailState, setReceivedMailState] =
    useState<ReceivedMailState>();
  const [tries, setTries] = useState<number>(0);
  const [hasInvalidOtpError, setHasInvalidOtpError] = useState<boolean>(false);
  const { isLoading, setLoadingState } = useLoading();

  const {
    dataStore: { mailOtpStore, userStore },
  } = useStore();

  const getOtpStatus = useCallback(async () => {
    try {
      setLoadingState('Loading');
      const result = await mailOtpStore.getMailOtpStatus(sourceId);
      setReceivedMailState(result);
      setLoadingState('Loaded');
    } catch (e) {
      console.error('Error fetching OTP status:', e);
      setLoadingState('Error');
      setReceivedMailState(ReceivedMailState.ERROR);
    }
  }, [mailOtpStore, sourceId, setLoadingState]);

  const handleSendMailClick = async (): Promise<boolean> => {
    try {
      setLoadingState('Loading');
      const { sent, reason } = await mailOtpStore.sendMail(
        source,
        sourceId,
        displayName,
      );

      if (!sent) {
        setReceivedMailState(reason ?? ReceivedMailState.ERROR);
        setLoadingState('Error');
        return false;
      }

      setTries(0);
      setHasInvalidOtpError(false);
      setReceivedMailState(ReceivedMailState.RECEIVED);
      setLoadingState('Loaded');
      return true;
    } catch (error: unknown) {
      console.error('Error sending mail:', error);
      setReceivedMailState(ReceivedMailState.ERROR);
      setLoadingState('Error');
      return false;
    }
  };

  const handleCheckOtpClick = useCallback(
    async (otp: string) => {
      try {
        setReceivedMailState(ReceivedMailState.CODE_CHECK);
        const response = await mailOtpStore.verifyMailOtp(sourceId, otp);
        await new Promise((resolve) => setTimeout(resolve, 300)); // So we can see the beautiful loader UWU

        if (response.verified) {
          setHasInvalidOtpError(false);
          setReceivedMailState(ReceivedMailState.VERIFIED);
        } else {
          if (response.reason === FailedMailOtpCodeReason.INVALID_CODE) {
            setTries(response.tries || 0);
            setHasInvalidOtpError(true);
            setReceivedMailState(ReceivedMailState.RECEIVED);
          } else if (response.reason === FailedMailOtpCodeReason.EXPIRED_CODE) {
            setTries(0);
            setHasInvalidOtpError(false);
            await getOtpStatus();
          }
        }

        setLoadingState('Loaded');
      } catch {
        setLoadingState('Error');
        setReceivedMailState(ReceivedMailState.ERROR);
      }
    },
    [getOtpStatus, mailOtpStore, setLoadingState, sourceId],
  );

  useEffect(() => {
    getOtpStatus();
  }, []);

  useEffect(() => {
    trackMixpanel({
      event: EventTypes.MAIL_OTP_SENT,
      properties: {
        email: userStore.currentUser.email,
        platform: EnvService.get().application,
        otpState: receivedMailState,
        source,
        sourceId,
        displayName,
        retries: tries,
      },
    });
  }, [tries, receivedMailState]);

  if (isLoading) {
    return (
      <Stack alignItems={'center'} padding={20}>
        <CircularProgress />
      </Stack>
    );
  }

  switch (receivedMailState) {
    case ReceivedMailState.NOT_RECEIVED:
      return (
        <NotReceivedMail
          header={sendCodeStageHeader}
          message={sendCodeStageMessage}
          handleSendMailClick={handleSendMailClick}
          isLoading={isLoading}
        />
      );

    case ReceivedMailState.RECEIVED:
      return (
        <MailOtpStage
          handleSendMailClick={handleSendMailClick}
          isLoading={isLoading}
          handleCheckOtpClick={handleCheckOtpClick}
          tries={tries}
          sourceId={sourceId}
          source={String(source)}
          hasInvalidOtpError={hasInvalidOtpError}
        />
      );

    case ReceivedMailState.CODE_CHECK:
      return <CodeCheck />;

    case ReceivedMailState.VERIFIED:
      return verifiedCodeComponent;

    case ReceivedMailState.NO_MORE_RETRIES:
      return <NoMoreRetries isLoading={isLoading} />;

    case ReceivedMailState.UNSUBSCRIBED:
      return (
        <MailRejected
          isLoading={isLoading}
          message={
            <Trans
              ns="mailConfirmationNova"
              i18nKey="unsubscribed_email_error"
              components={{ br: <br /> }}
            />
          }
        />
      );

    case ReceivedMailState.SOFT_BOUNCE:
      return (
        <MailRejected
          isLoading={isLoading}
          message={
            <Trans
              ns="mailConfirmationNova"
              i18nKey="soft_bounce_error"
              components={{ br: <br /> }}
            />
          }
        />
      );

    case ReceivedMailState.ERROR:
      return <FailedSendingMail isLoading={isLoading} />;

    default:
      return null;
  }
};
